/**
 * Release Train generator: compute the version/date value for every `data-ph`
 * placeholder slot from a small config, so regenerating rewrites only those
 * spans and never touches edited prose.
 *
 * Slots name the ABSOLUTE version index Vk (V0 = the anchor GA) and
 * week-relative dates. The train's invariant — the RC cut this cycle becomes
 * next cycle's GA — means a single version sequence (V0, V1, V2 …) covers GA
 * (Vk), RC (Vk+1) and alpha (Vk+2) for every week.
 */
export interface TrainConfig {
  anchorMonday: string // ISO date (YYYY-MM-DD): a REFERENCE Monday…
  anchorGa: string // …on which the GA version was this, e.g. "1.10.0" (= V0 then)
  cadenceWeeks: number // weeks between cycles: 1 now, 2 later
}

const DAY_MS = 86_400_000

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const DASH = "–" // en dash, matches the template's &ndash;
const WEEKS_SHOWN = 3 // Previous, Current, Next (Week 2 is the current cycle)

function parseDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}
function addDays(base: Date, n: number): Date {
  const d = new Date(base.getTime())
  d.setDate(d.getDate() + n)
  return d
}
/** Monday (local midnight) of the ISO week containing `d`. Sat/Sun map back to
 *  the Monday that week started on. */
function mondayOf(d: Date): Date {
  const day = d.getDay() // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff)
}

/** All six string formats a version appears in across the document. */
function versionFormats(major: number, minor: number): Record<string, string> {
  return {
    bare: `${major}.${minor}`,
    tag: `v${major}.${minor}.0`,
    full: `${major}.${minor}.0`,
    branch: `release/${major}.${minor}`,
    hotfix: `hotfix/${major}.${minor}/*`,
    rc1: `${major}.${minor}.0-rc.1`,
  }
}

/** slot -> value for every placeholder, advanced to the cycle current on
 *  `today`. The stored config is a fixed reference point (on anchorMonday the
 *  GA was anchorGa); the displayed Week 2 is always the current cycle (Week 1 the
 *  previous, Week 3 the next), so the numbers roll forward on their own as real
 *  dates pass. */
export function computeSlots(cfg: TrainConfig, today: Date = new Date()): Record<string, string> {
  const map: Record<string, string> = {}

  const m = /^(\d+)\.(\d+)/.exec(cfg.anchorGa.trim())
  const major = m ? Number(m[1]) : 1
  const minor0 = m ? Number(m[2]) : 0
  const cadence = cfg.cadenceWeeks > 0 ? cfg.cadenceWeeks : 1

  // Cadence-cycles elapsed from the reference Monday to the cycle CONTAINING
  // today. Round the Monday-to-Monday gap to whole weeks first — DST makes a real
  // week 7d ± 1h, so flooring the raw millisecond gap could slip a week — then
  // floor by the cadence so a biweekly cycle only advances after its full span.
  const epoch = mondayOf(parseDate(cfg.anchorMonday))
  const weeks = Math.round((mondayOf(today).getTime() - epoch.getTime()) / (7 * DAY_MS))
  const steps = Math.floor(weeks / cadence)
  // Centre the shown window on today: Week 1 = the PREVIOUS cycle, Week 2 = the
  // current, Week 3 = the next. (For cadence > 1 these are release cycles cadence
  // weeks apart, not adjacent calendar weeks.)
  // anchorMonday is a FIXED reference in the past and today only moves forward,
  // so steps >= 0 and the Week-1 (previous) minor stays >= minor0 - 1. A future
  // anchor (misconfiguration) could drive the minor negative — out of scope.
  const base = addDays(epoch, (steps - 1) * 7 * cadence) // Week-1 (previous) Monday
  const v0 = minor0 + steps - 1 // GA minor of Week 1 (previous cycle)

  // Versions V0 … V(WEEKS_SHOWN+1): week w needs GA=V(w-1), RC=V(w), alpha=V(w+1).
  for (let k = 0; k <= WEEKS_SHOWN + 1; k++) {
    const fmts = versionFormats(major, v0 + k)
    for (const [fmt, val] of Object.entries(fmts)) map[`v${k}.${fmt}`] = val
  }

  for (let w = 1; w <= WEEKS_SHOWN; w++) {
    const monday = addDays(base, (w - 1) * 7 * cadence)
    const days = [0, 1, 2, 3, 4].map((i) => addDays(monday, i))
    days.forEach((d, i) => {
      map[`d${w}.${i}`] = `${d.getDate()} ${MONTHS[d.getMonth()]}`
      map[`dn${w}.${i}`] = `${d.getDate()}`
    })
    const mon = days[0]
    const fri = days[4]
    map[`wk${w}.span`] =
      mon.getMonth() === fri.getMonth()
        ? `${mon.getDate()}${DASH}${fri.getDate()} ${MONTHS[fri.getMonth()]}`
        : `${mon.getDate()} ${MONTHS[mon.getMonth()]}${DASH}${fri.getDate()} ${MONTHS[fri.getMonth()]}`
  }
  return map
}

function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${m}-${day}`
}

/** ISO (YYYY-MM-DD) Mondays of the three shown weeks — [previous, current, next]
 *  — for `today`, using the same cycle math as `computeSlots`. Completion is keyed
 *  by these so a task's checkbox is remembered against the exact calendar week it
 *  was completed for (and a new week therefore starts unchecked). */
export function weekMondays(cfg: TrainConfig, today: Date = new Date()): string[] {
  const cadence = cfg.cadenceWeeks > 0 ? cfg.cadenceWeeks : 1
  const epoch = mondayOf(parseDate(cfg.anchorMonday))
  const weeks = Math.round((mondayOf(today).getTime() - epoch.getTime()) / (7 * DAY_MS))
  const steps = Math.floor(weeks / cadence)
  const base = addDays(epoch, (steps - 1) * 7 * cadence)
  return [0, 1, 2].map((w) => isoDate(addDays(base, w * 7 * cadence)))
}

/** Rewrite the text of every `[data-ph]` span under `root` from the slot map. */
export function applySlots(root: ParentNode, map: Record<string, string>): number {
  let n = 0
  root.querySelectorAll("[data-ph]").forEach((el) => {
    const slot = el.getAttribute("data-ph")
    if (slot && map[slot] != null) {
      el.textContent = map[slot]
      n++
    }
  })
  return n
}
